import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
} from "@/components/ui/sheet";
import { useToast } from "@/components/ui/use-toast";
import { useEffect, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { LoadingButton } from "@/components/ui/common/loading-button";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { MultiSelect } from "@/components/ui/multiselect/multiselectCheckbox";
import { FormProps, ErrorType } from "@/types";
import { FacilityUnavailability } from "@/types/facility/unavailability";
import {
  useCreateFacilityUnavailabilityMutation,
  useUpdateFacilityUnavailabilityMutation,
  useGetFacilitiesQuery,
} from "@/services/facilityApis";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";

const FacilityUnavailabilityForm = ({
  isOpen,
  setOpen,
  action,
  setAction,
  data,
  setData,
  refetch,
}: FormProps<FacilityUnavailability>) => {
  const { toast } = useToast();
  const [createFacilityUnavailability] = useCreateFacilityUnavailabilityMutation();
  const [updateFacilityUnavailability] = useUpdateFacilityUnavailabilityMutation();

  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) ?? 0;

  const { data: facilitiesData } = useGetFacilitiesQuery(
    {
      query: "status=active&bookable=true&sort_key=id&sort_order=desc",
      orgId: orgId,
    },
    { skip: !orgId || !isOpen }
  );

  const facilityOptions = useMemo(() => {
  return Array.isArray(facilitiesData?.data)
    ? facilitiesData.data
        .filter((facility) => facility.bookable === true)
        .map((facility) => ({
          label: facility.name || "",
          value: facility.id as number,
        }))
    : [];
}, [facilitiesData]);

  const initialValues: FacilityUnavailability = {
    facility_ids: [],
    start_date: "",
    end_date: "",
    reason: "",
  };

  const {
    control,
    handleSubmit,
    reset,
    clearErrors,
    watch,
    formState: { isSubmitting, errors },
  } = useForm<FacilityUnavailability>({
    mode: "all",
    defaultValues: initialValues,
  });

  const watchedStartDate = watch("start_date");

  useEffect(() => {
    if (!isOpen) return;
    if (action === "edit" && data) {
      reset({
        ...data,
        facility_ids:
          data.facility_ids && data.facility_ids.length > 0
            ? data.facility_ids
            : data.facility_id
            ? [data.facility_id]
            : [],
      });
    } else {
      clearErrors();
      reset(initialValues);
    }
  }, [action, isOpen]);

  const onSubmit = async (formData: FacilityUnavailability) => {
    try {
      const payload = {
        ...formData,
        facility_ids: (formData.facility_ids ?? []).map((id) => Number(id)),
        org_id: orgId,
      };

      if (action === "add") {
        await createFacilityUnavailability(payload).unwrap();
        toast({
          description: "Facility unavailability created successfully",
          variant: "success",
        });
      } else {
        await updateFacilityUnavailability(payload).unwrap();
        toast({
          description: "Facility unavailability updated successfully",
          variant: "success",
        });
      }
      refetch();
      handleClose();
    } catch (error: unknown) {
      console.error("Error", { error });
      if (error && typeof error === "object" && "data" in error) {
        const typedError = error as ErrorType;
        toast({
          variant: "destructive",
          title: "Error in form Submission",
          description: `${
            typedError.data?.detail ||
            (typedError.data as { message?: string }).message
          }`,
        });
      } else {
        toast({
          variant: "destructive",
          title: "Error in form Submission",
          description: `Something Went Wrong.`,
        });
      }
    }
  };

  const onError = () => {
    toast({
      variant: "destructive",
      description: "Please fill all the mandatory fields",
    });
  };

  const handleClose = () => {
    clearErrors();
    setAction("add");
    setData(undefined);
    reset(initialValues);
    setOpen(false);
  };

  return (
    <Sheet open={isOpen} onOpenChange={handleClose}>
      <SheetContent
        hideCloseButton
        className="bg-formbg w-full lg:!max-w-[600px] p-0 custom-scrollbar h-full"
      >
        <form
          noValidate
          className="pb-4 h-full flex flex-col"
          onSubmit={handleSubmit(onSubmit, onError)}
        >
          {/* Header */}
          <SheetHeader className="px-5 sticky top-0 z-40 py-2 bg-white shadow-sm">
            <div className="flex items-center justify-between gap-2 w-full">
              <div className="flex items-center gap-3">
                <button type="button" onClick={handleClose}>
                  <i className="fa fa-xmark text-xl"></i>
                </button>
                <h1 className="font-medium">
                  {action === "add" ? "Create" : "Edit"} Facility Unavailability
                </h1>
              </div>
              <LoadingButton
                loading={isSubmitting}
                disabled={isSubmitting}
                type="submit"
                className="w-[80px]"
              >
                {!isSubmitting && <i className="fa fa-floppy-disk pr-2"></i>}
                {action === "add" ? "Save" : "Update"}
              </LoadingButton>
            </div>
          </SheetHeader>

          {/* Body */}
          <SheetDescription className="flex-1 p-6 overflow-y-auto">
            <Card className="p-4">
              <p className="font-medium text-base">Unavailability Details</p>
              <Separator className="h-[1px] my-2" />
              <CardContent className="py-4 px-0 space-y-4">
                {/* Select Facility (Multi) */}
                <div className="space-y-1 relative">
                  <Label>
                    Select Facility <span className="text-red-500">*</span>
                  </Label>
                  <Controller
                    name="facility_ids"
                    control={control}
                    rules={{
                      validate: (value) =>
                        (value && value.length > 0) ||
                        "Please select at least one facility",
                    }}
                    render={({ field: { value, onChange } }) => (
                      <MultiSelect
                        options={facilityOptions}
                        onValueChange={(selectedValues) => {
                          onChange(selectedValues as number[]);
                        }}
                        defaultValue={Array.isArray(value) ? value : []}
                        placeholder="Select Facility"
                        variant="inverted"
                        maxCount={2}
                      />
                    )}
                  />
                  {errors.facility_ids && (
                    <span className="text-red-500 text-xs">
                      {errors.facility_ids.message}
                    </span>
                  )}
                </div>

                {/* Start Date */}
                <div className="space-y-1">
                  <Label>
                    Start Date <span className="text-red-500">*</span>
                  </Label>
                  <Controller
                    name="start_date"
                    control={control}
                    rules={{ required: "Required" }}
                    render={({ field: { value, onChange } }) => (
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            className={cn(
                              "w-full justify-start text-left font-normal",
                              !value && "text-muted-foreground"
                            )}
                          >
                            {value
                              ? format(new Date(value), "dd-MM-yyyy")
                              : "Pick a date"}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            captionLayout="dropdown-buttons"
                            fromYear={new Date().getFullYear()}
                            toYear={new Date().getFullYear() + 20}
                            selected={value ? new Date(value) : undefined}
                            onSelect={(d) =>
                              d && onChange(format(d, "yyyy-MM-dd"))
                            }
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    )}
                  />
                  {errors.start_date && (
                    <span className="text-red-500 text-xs">
                      {errors.start_date.message}
                    </span>
                  )}
                </div>

                {/* End Date */}
                <div className="space-y-1">
                  <Label>
                    End Date <span className="text-red-500">*</span>
                  </Label>
                  <Controller
                    name="end_date"
                    control={control}
                    rules={{
                      required: "Required",
                      validate: (value) => {
                        if (watchedStartDate && value && value < watchedStartDate) {
                          return "End date must be on or after start date";
                        }
                        return true;
                      },
                    }}
                    render={({ field: { value, onChange } }) => (
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            className={cn(
                              "w-full justify-start text-left font-normal",
                              !value && "text-muted-foreground"
                            )}
                          >
                            {value
                              ? format(new Date(value), "dd-MM-yyyy")
                              : "Pick a date"}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            captionLayout="dropdown-buttons"
                            fromYear={new Date().getFullYear()}
                            toYear={new Date().getFullYear() + 20}
                            selected={value ? new Date(value) : undefined}
                            defaultMonth={
                              watchedStartDate ? new Date(watchedStartDate) : new Date()
                            }
                            onSelect={(d) =>
                              d && onChange(format(d, "yyyy-MM-dd"))
                            }
                            disabled={(d) => {
                              const today = new Date();
                              today.setHours(0, 0, 0, 0);
                              const start = watchedStartDate
                                ? new Date(watchedStartDate)
                                : today;
                              start.setHours(0, 0, 0, 0);
                              return d < start;
                            }}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    )}
                  />
                  {errors.end_date && (
                    <span className="text-red-500 text-xs">
                      {errors.end_date.message}
                    </span>
                  )}
                </div>

                {/* Reason */}
                <div className="space-y-1">
                  <Label>
                    Reason <span className="text-red-500">*</span>
                  </Label>
                  <Controller
                    name="reason"
                    control={control}
                    rules={{
                      required: "Required",
                      maxLength: {
                        value: 500,
                        message: "Reason should not exceed 500 characters",
                      },
                      validate: (value) => {
                        if (!value || value.trim().length === 0) {
                          return "Reason should not be empty";
                        }
                        return true;
                      },
                    }}
                    render={({ field }) => (
                      <textarea
                        {...field}
                        rows={4}
                        placeholder="Enter reason for unavailability"
                        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
                      />
                    )}
                  />
                  {errors.reason && (
                    <span className="text-red-500 text-xs">
                      {errors.reason.message}
                    </span>
                  )}
                </div>
              </CardContent>
            </Card>
          </SheetDescription>
        </form>
      </SheetContent>
    </Sheet>
  );
};

export default FacilityUnavailabilityForm;